import { useEffect, useRef, useState } from "react";

export function useSpeechActivityWatcher({
  threshold = 0.02,
  smoothingDelay = 10, // milliseconds
  audioStream,
  onSpeech,
  onSilence,
} = {}) {
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const [isTalking, setIsTalking] = useState(false);
  const speechTimeoutRef = useRef(null);
  const silenceTimeoutRef = useRef(null);

  const calculateVolume = (dataArray) => {
    let sumSquares = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = (dataArray[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    return Math.sqrt(sumSquares / dataArray.length);
  };

  const start = () => {
    audioContextRef.current = new AudioContext();
    const source = audioContextRef.current.createMediaStreamSource(audioStream);
    sourceRef.current = source;

    const analyser = audioContextRef.current.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.fftSize);

    const check = () => {
      analyser.getByteTimeDomainData(dataArray);
      const volume = calculateVolume(dataArray);

      const above = volume > threshold;

      if (above) {
        clearTimeout(silenceTimeoutRef.current);
        if (!isTalking && !speechTimeoutRef.current) {
          setIsTalking(true);
          if (onSpeech) onSpeech();
          speechTimeoutRef.current = null;
        }
      } else {
        if (!silenceTimeoutRef.current) {
          clearTimeout(speechTimeoutRef.current);
          silenceTimeoutRef.current = setTimeout(() => {
            setIsTalking(false);
            if (onSilence) onSilence();
            silenceTimeoutRef.current = null;
          }, smoothingDelay);
        }
      }

      animationFrameRef.current = requestAnimationFrame(check);
    };

    check();
  };

  const stop = () => {
    cancelAnimationFrame(animationFrameRef.current);
    clearTimeout(speechTimeoutRef.current);
    clearTimeout(silenceTimeoutRef.current);

    audioContextRef.current?.close();

    analyserRef.current = null;
    sourceRef.current = null;
    audioContextRef.current = null;
    animationFrameRef.current = null;
    speechTimeoutRef.current = null;
    silenceTimeoutRef.current = null;

    setIsTalking(false);
  };

  return {
    start,
    stop,
    isTalking,
  };
}
